// Source: KL-Engraving-App/extension/laser.js.
const BAUD_RATE = 460800;
const USB_FILTERS = [{ usbVendorId: 0x1a86 }];
const BYTE_LOG_LIMIT = 4096;
const DIAGNOSTIC_BYTE_LIMIT = 512;
const STATUS_QUERIES = ["?", "?\n", "?\r\n"];
const LP_STATUS_FRAME = [0xaa, 0xbb, 0x08, 0, 0, 0, 0, 0, 0, 0, 0];

const decodeLatin1 = (bytes) => Array.from(bytes, (byte) => String.fromCharCode(byte)).join("");

const escapeDiagnosticText = (text) => Array.from(text, (character) => {
  const code = character.charCodeAt(0);
  if (code === 13) return "\\r";
  if (code === 10) return "\\n";
  if (code < 32 || (code >= 127 && code <= 159)) return `\\x${code.toString(16).padStart(2, "0")}`;
  return character;
}).join("");

const parseLaserpeckerFrame = (bytes) => {
  let start = -1;
  for (let index = 0; index < bytes.length - 1; index += 1) {
    if (bytes[index] === 0xaa && bytes[index + 1] === 0xbb) {
      start = index;
      break;
    }
  }
  if (start === -1 || bytes.length - start < 3) return null;

  const length = bytes[start + 2];
  if (bytes.length - start - 3 < length) return null;
  const frame = bytes.slice(start, start + length + 3);
  const checksum = frame.slice(3, length + 1).reduce((sum, byte) => sum + byte, 0) & 0xffff;
  const expectedChecksum = (frame[length + 1] << 8) | frame[length + 2];
  return {
    frameHex: Array.from(frame, (byte) => byte.toString(16).padStart(2, "0")).join(" "),
    valid: checksum === expectedChecksum,
    func: frame[3],
    mode: frame[4],
    wState: frame[5],
    error: frame[9],
  };
};

export class Laser {
  constructor() {
    this.port = null;
    this.reader = null;
    this.pending = null;
    this.statusWaiters = [];
    this.statusQuery = null;
    this.statusProbeFailedAt = 0;
    this.idleCheck = null;
    this.rawReplies = [];
    this.machineState = null;
    this.busy = false;
    this.stopRequested = false;
    this.listeners = new Set();
    this.encoder = new TextEncoder();
    this.buffer = "";
    this.byteLog = [];
    this.totalBytes = 0;
    if (typeof navigator !== "undefined" && navigator.serial) {
      navigator.serial.addEventListener("connect", (event) => {
        if (!this.port) this.connectPort(event.target?.port ?? event.port).catch(() => this.emit());
      });
      navigator.serial.addEventListener("disconnect", (event) => {
        if (event.port === this.port) this.handleDisconnect();
      });
      navigator.serial.getPorts().then((ports) => {
        if (!this.port && ports.length) this.connectPort(ports[0]).catch(() => this.emit());
      }).catch(() => this.emit());
    }
  }

  get connected() {
    return Boolean(this.port?.readable && this.port?.writable);
  }

  getDiagnostics() {
    const bytes = this.byteLog.slice(-DIAGNOSTIC_BYTE_LIMIT);
    let portInfo = null;
    try {
      portInfo = this.port?.getInfo?.() || null;
    } catch {
      portInfo = null;
    }
    return {
      portInfo,
      baudRate: BAUD_RATE,
      dtr: false,
      connected: this.connected,
      busy: this.busy,
      machineState: this.machineState,
      statusQuery: this.statusQuery === null ? null : escapeDiagnosticText(this.statusQuery),
      totalBytes: this.totalBytes,
      hex: bytes.map((byte) => byte.toString(16).padStart(2, "0")).join(" "),
      text: escapeDiagnosticText(decodeLatin1(bytes)),
      pendingBuffer: escapeDiagnosticText(this.buffer),
      rawReplies: [...this.rawReplies],
    };
  }

  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  emit() {
    for (const listener of this.listeners) listener(this);
  }

  async requestPort(showAll = false) {
    if (typeof navigator === "undefined" || !navigator.serial) throw new Error("Web Serial is unavailable in this browser");
    const port = await navigator.serial.requestPort(showAll ? {} : { filters: USB_FILTERS });
    await this.connectPort(port);
    return port;
  }

  async connectPort(port) {
    if (!port) throw new Error("No serial port selected");
    if (this.port === port && this.connected) return;
    this.byteLog = [];
    this.totalBytes = 0;
    this.rawReplies = [];
    this.machineState = null;
    this.buffer = "";
    this.statusQuery = null;
    this.statusProbeFailedAt = 0;
    this.idleCheck = null;
    this.port = port;
    try {
      await port.open({ baudRate: BAUD_RATE });
      try {
        await port.setSignals({ dataTerminalReady: false });
      } catch {
        // Some adapters do not support changing DTR.
      }
      this.reader = port.readable.getReader();
      this.emit();
      this.readLoop(port, this.reader);
    } catch (error) {
      this.port = null;
      this.reader = null;
      this.emit();
      throw error;
    }
  }

  async readLoop(port, reader) {
    try {
      while (port === this.port) {
        const { value, done } = await reader.read();
        if (done) break;
        this.totalBytes += value.byteLength;
        for (const byte of value) this.byteLog.push(byte);
        if (this.byteLog.length > BYTE_LOG_LIMIT) this.byteLog.splice(0, this.byteLog.length - BYTE_LOG_LIMIT);
        this.buffer += decodeLatin1(value);
        const lines = this.buffer.split(/\r\n|\r|\n/);
        this.buffer = lines.pop() ?? "";
        for (const line of lines) this.processLine(line);
      }
    } catch {
      if (port === this.port) this.handleDisconnect();
    } finally {
      try {
        reader.releaseLock();
      } catch {
        // Reader may already be unlocked after a disconnect.
      }
      if (port === this.port) this.handleDisconnect();
    }
  }

  processLine(line) {
    if (!line || !line.trim()) return;
    this.rawReplies.push(line);
    if (this.rawReplies.length > 100) this.rawReplies.shift();
    const status = line.match(/<\s*([A-Za-z]+)(?::\d+)?(?=[|,>:]|$)/);
    if (status) {
      this.machineState = status[1];
      const waiters = this.statusWaiters.splice(0);
      for (const waiter of waiters) {
        if (waiter.expected === this.machineState || waiter.expected === "*") {
          clearTimeout(waiter.timer);
          waiter.resolve(line);
        } else {
          this.statusWaiters.push(waiter);
        }
      }
      this.emit();
      return;
    }
    const reply = line.trim();
    if (reply === "ok") {
      this.resolvePending();
      return;
    }
    if (/^(error:\d+|ALARM:\d+)$/i.test(reply)) {
      this.rejectPending(new Error(reply));
      this.emit();
    }
  }

  resolvePending() {
    if (!this.pending) return;
    const { resolve, timer } = this.pending;
    clearTimeout(timer);
    this.pending = null;
    resolve();
  }

  rejectPending(error) {
    if (!this.pending) return;
    const { reject, timer } = this.pending;
    clearTimeout(timer);
    this.pending = null;
    reject(error);
  }

  async writeBytes(bytes) {
    if (!this.connected) throw new Error("Laser not connected");
    const writer = this.port.writable.getWriter();
    try {
      await writer.write(bytes);
    } finally {
      writer.releaseLock();
    }
  }

  async writeRaw(value) {
    await this.writeBytes(this.encoder.encode(value));
  }

  async writeLine(line, timeoutMs = 10000) {
    if (this.pending) throw new Error("Laser is already waiting for a response");
    let resolveResponse;
    let rejectResponse;
    const response = new Promise((resolve, reject) => {
      resolveResponse = resolve;
      rejectResponse = reject;
    });
    const timer = setTimeout(() => {
      this.rejectPending(new Error(`Timed out waiting for response to ${line}`));
    }, timeoutMs);
    this.pending = { resolve: resolveResponse, reject: rejectResponse, timer };
    try {
      await this.writeRaw(`${line}\n`);
      await response;
    } catch (error) {
      if (this.pending?.reject === rejectResponse) this.rejectPending(error);
      throw error;
    }
  }

  waitForStatus(expected, timeoutMs = 2000) {
    let waiter;
    let resolveWaiter;
    const promise = new Promise((resolve, reject) => {
      resolveWaiter = resolve;
      waiter = {
        expected,
        resolve,
        reject,
        timer: setTimeout(() => {
          this.statusWaiters = this.statusWaiters.filter((candidate) => candidate !== waiter);
          reject(new Error(`Timed out waiting for laser status ${expected}`));
        }, timeoutMs),
      };
      this.statusWaiters.push(waiter);
    });
    promise.cancel = () => {
      if (!this.statusWaiters.includes(waiter)) return;
      clearTimeout(waiter.timer);
      this.statusWaiters = this.statusWaiters.filter((candidate) => candidate !== waiter);
      resolveWaiter(null);
    };
    return promise;
  }

  async queryStatus(query, timeoutMs = 1500) {
    const startedAt = Date.now();
    const bytesBefore = this.totalBytes;
    const statusWait = this.waitForStatus("*", timeoutMs);
    let outcome;
    let ok = false;
    try {
      await this.writeRaw(query);
      const status = await statusWait;
      outcome = `status ${status}`;
      ok = true;
    } catch (error) {
      statusWait.cancel();
      const message = error.message || String(error);
      outcome = message.startsWith("Timed out waiting for laser status") ? "timeout" : `write failed: ${message}`;
    }
    return {
      query,
      outcome,
      ms: Date.now() - startedAt,
      bytes: this.totalBytes - bytesBefore,
      ok,
    };
  }

  async probeStatusQuery() {
    const results = [];
    this.statusQuery = null;
    for (const query of STATUS_QUERIES) {
      results.push(await this.queryStatus(query));
    }
    this.statusQuery = results.find((result) => result.ok)?.query || null;
    this.statusProbeFailedAt = this.statusQuery === null ? Date.now() : 0;
    return results;
  }

  async queryBinaryStatus(timeoutMs = 1000) {
    const startedAt = Date.now();
    const startIndex = this.totalBytes;
    const byteLogLengthAtStart = this.byteLog.length;
    let receivedBytes = [];
    let frame = null;
    let writeError = null;
    const bytesSinceStart = () => {
      const byteCount = Math.min(Math.max(0, this.totalBytes - startIndex), this.byteLog.length);
      const tailStart = this.byteLog.length - byteCount;
      return this.byteLog.slice(Math.min(byteLogLengthAtStart, tailStart));
    };

    try {
      await this.writeBytes(new Uint8Array(LP_STATUS_FRAME));
      while (Date.now() - startedAt < timeoutMs) {
        receivedBytes = bytesSinceStart();
        frame = parseLaserpeckerFrame(receivedBytes);
        if (frame) break;
        await new Promise((resolve) => setTimeout(resolve, 50));
      }
      if (!frame) {
        receivedBytes = bytesSinceStart();
        frame = parseLaserpeckerFrame(receivedBytes);
      }
    } catch (error) {
      writeError = error.message || String(error);
    }

    await new Promise((resolve) => setTimeout(resolve, 100));
    this.buffer = "";
    receivedBytes = bytesSinceStart();
    const bytes = Math.max(0, this.totalBytes - startIndex);
    const ms = Date.now() - startedAt;
    const query = "LP binary status (aa bb 08 00…)";
    if (writeError) return { query, outcome: `write failed: ${writeError}`, ms, bytes, ok: false };
    if (frame) {
      return {
        query,
        outcome: `frame ${frame.frameHex} (checksum ${frame.valid ? "ok" : "bad"}) mode=${frame.mode} w_state=${frame.wState} error=${frame.error}`,
        ms,
        bytes,
        ok: frame.valid,
      };
    }
    if (receivedBytes.length) {
      const hex = Array.from(receivedBytes, (byte) => byte.toString(16).padStart(2, "0")).join(" ");
      return { query, outcome: `no frame: ${hex}`, ms, bytes, ok: false };
    }
    return { query, outcome: "timeout", ms, bytes, ok: false };
  }

  async isIdle() {
    if (!this.connected || this.busy) return false;
    if (this.idleCheck) return this.idleCheck;
    const idleCheck = (async () => {
      if (this.statusQuery === null) {
        if (Date.now() - this.statusProbeFailedAt < 10000) return false;
        await this.probeStatusQuery();
        if (this.statusQuery === null) return false;
      }
      const result = await this.queryStatus(this.statusQuery, 2000);
      return result.ok && this.machineState === "Idle";
    })();
    this.idleCheck = idleCheck;
    try {
      return await idleCheck;
    } finally {
      if (this.idleCheck === idleCheck) this.idleCheck = null;
    }
  }

  async waitUntilIdle({ timeoutMs = 120000, intervalMs = 250 } = {}) {
    if (!this.connected) throw new Error("Laser not connected");
    const startedAt = Date.now();

    while (Date.now() - startedAt < timeoutMs) {
      if (!this.connected) throw new Error("Laser disconnected");
      if (this.statusQuery === null) {
        if (Date.now() - this.statusProbeFailedAt >= 10000) await this.probeStatusQuery();
      }
      if (!this.busy && this.statusQuery !== null) {
        const remaining = timeoutMs - (Date.now() - startedAt);
        if (remaining <= 0) break;
        const result = await this.queryStatus(this.statusQuery, Math.min(2000, remaining));
        if (result.ok) {
          const status = result.outcome.slice("status ".length);
          const state = status.match(/<\s*([A-Za-z]+)(?::\d+)?(?=[|,>:]|$)/)?.[1];
          if (state === "Idle") {
            const mpos = status.match(/MPos:([+-]?(?:\d+(?:\.\d*)?|\.\d+)),([+-]?(?:\d+(?:\.\d*)?|\.\d+))/);
            return {
              status,
              mpos: mpos ? { x: Number(mpos[1]), y: Number(mpos[2]) } : null,
              waitedMs: Date.now() - startedAt,
            };
          }
        }
      }
      const remaining = timeoutMs - (Date.now() - startedAt);
      if (remaining <= 0) break;
      await new Promise((resolve) => setTimeout(resolve, Math.min(intervalMs, remaining)));
    }

    throw new Error("Timed out waiting for laser status Idle");
  }

  async readInfo() {
    if (!this.connected) throw new Error("Laser not connected");
    if (this.busy) throw new Error("Laser is engraving");
    const queries = [];
    const startedAt = Date.now();
    const bytesBefore = this.totalBytes;
    let outcome = "ok";
    try {
      await this.writeLine("$I", 1500);
    } catch (error) {
      const message = error.message || String(error);
      const replyError = message.match(/^(error:\d+|ALARM:\d+)/i);
      if (replyError) outcome = replyError[0];
      else if (message.startsWith("Timed out waiting for response")) outcome = "timeout";
      else outcome = `write failed: ${message}`;
    }
    queries.push({
      query: "$I",
      outcome,
      ms: Date.now() - startedAt,
      bytes: this.totalBytes - bytesBefore,
    });

    const statusQueries = await this.probeStatusQuery();
    queries.push(...statusQueries.map((result) => ({
      ...result,
      query: escapeDiagnosticText(result.query),
    })));
    queries.push(await this.queryBinaryStatus());
    return { queries, statusQuery: this.statusQuery, ...this.getDiagnostics() };
  }

  async run(lines, onProgress = () => {}) {
    if (!this.connected) throw new Error("Laser not connected");
    if (this.busy) throw new Error("Laser is already engraving");
    this.busy = true;
    this.stopRequested = false;
    this.emit();
    try {
      for (let index = 0; index < lines.length; index += 1) {
        if (this.stopRequested) throw new Error("Engraving stopped");
        await this.writeLine(lines[index]);
        onProgress((index + 1) / lines.length);
      }
    } catch (error) {
      if (!this.stopRequested) await this.stopSequence();
      throw error;
    } finally {
      this.busy = false;
      this.emit();
    }
  }

  async stopSequence() {
    this.stopRequested = true;
    this.rejectPending(new Error("Engraving stopped"));
    try {
      await this.writeRaw("!");
      await this.writeRaw("\x18");
      await this.writeRaw("M5\n");
    } catch {
      // A disconnected controller cannot receive the stop sequence.
    }
    this.emit();
  }

  async stop() {
    if (!this.connected) {
      this.stopRequested = true;
      this.rejectPending(new Error("Engraving stopped"));
      return;
    }
    await this.stopSequence();
  }

  handleDisconnect() {
    this.rejectPending(new Error("Laser disconnected"));
    for (const waiter of this.statusWaiters.splice(0)) {
      clearTimeout(waiter.timer);
      waiter.reject(new Error("Laser disconnected"));
    }
    this.port = null;
    this.reader = null;
    this.machineState = null;
    this.statusQuery = null;
    this.statusProbeFailedAt = 0;
    this.idleCheck = null;
    this.emit();
  }
}
